import React, { useState, useMemo } from 'react';
import { 
  Code, 
  Cpu, 
  Zap, 
  ArrowRight, 
  Cast, 
  Network, 
  Settings,
  Clock,
  Users,
  Award
} from 'lucide-react';
import logo from '../../../assets/tps.png';
import Header from './Header';

export default function TaskStreamAI() {
  const [activeSection, setActiveSection] = useState('overview');
  const [activeCapability, setActiveCapability] = useState(null);
  const [activeImpact, setActiveImpact] = useState(null);
  const [activeBenefit, setActiveBenefit] = useState(null);

  // Generate stable particle IDs and styles once on mount
  const particles = useMemo(() => {
    return Array.from({ length: 100 }).map((_, i) => ({
      id: `particle-${i}`,
      style: {
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${Math.random() * 5 + 2}px`,
        height: `${Math.random() * 5 + 2}px`,
        opacity: Math.random() * 0.5,
        animation: `float ${Math.random() * 20 + 10}s linear infinite`,
        animationDelay: `${Math.random() * 10}s`
      }
    }));
  }, []);

  const keyCapabilities = [
    {
      id: 'capability-code-generation',
      icon: <Code className="text-blue-400" size={32} />,
      title: "AI-Powered Code Generation",
      description: "Automatically generates code from user stories, eliminating routine manual tasks"
    },
    {
      id: 'capability-workload-management',
      icon: <Network className="text-purple-400" size={32} />,
      title: "Intelligent Workload Management",
      description: "Distributes tasks based on priority and developer availability"
    },
    {
      id: 'capability-optimization',
      icon: <Zap className="text-green-400" size={32} />,
      title: "Optimized Development Flow",
      description: "Reduces bottlenecks and supports faster, more efficient delivery"
    }
  ];

  const impactItems = [
    {
      id: 'impact-code-generation',
      icon: <Code className="text-blue-400" size={32} />,
      title: "Code Generation",
      description: "Interpret user stories to automatically generate precise, context-aware code"
    },
    {
      id: 'impact-workload-distribution',
      icon: <Network className="text-purple-400" size={32} />,
      title: "Workload Distribution",
      description: "Intelligent task allocation based on developer skills and current availability"
    },
    {
      id: 'impact-workflow-optimization',
      icon: <Zap className="text-green-400" size={32} />,
      title: "Workflow Optimization",
      description: "Reduce bottlenecks and streamline development processes for maximum efficiency"
    }
  ];

  const benefitItems = [
    {
      id: 'benefit-faster-delivery',
      icon: <Clock className="text-yellow-400" size={32} />,
      title: "Faster Delivery",
      description: "Accelerate project timelines by automating routine tasks"
    },
    {
      id: 'benefit-focus-innovation',
      icon: <Users className="text-indigo-400" size={32} />,
      title: "Focus on Innovation",
      description: "Free developers to tackle complex, creative challenges"
    },
    {
      id: 'benefit-efficiency',
      icon: <Award className="text-red-400" size={32} />,
      title: "Enhanced Efficiency",
      description: "Optimize resource allocation and team productivity"
    }
  ];

  return (
    <div className="relative bg-gradient-to-b from-gray-900 via-gray-900 to-indigo-900 min-h-screen text-white overflow-hidden">
      <Header />
      <div
        className="relative bg-gradient-to-b from-transparent via-blue-900 to-blue-900 min-h-screen text-white overflow-hidden"
        style={{ backgroundColor: 'rgba(58, 89, 209, 0.8)' }}
      >
        {/* Animated Background Particles */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {particles.map((particle) => (
            <div
              key={particle.id}
              className="absolute rounded-full bg-purple-500"
              style={particle.style}
            />
          ))}
        </div>

        <div className="max-w-6xl mx-auto px-4 py-20 relative">
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block relative mb-6">
              <div className="center">
                <img 
                  src={logo}
                  alt="cheetah Logo" 
                  className="w-[250px] h-[250px] object-contain"
                />
              </div>
            </div>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              AI-Powered Work Allocation and Code Automation within BlueGecko
            </p>
          </div>

          {/* Navigation Tabs */}
          <div className="flex justify-center mb-12">
            {[
              { key: 'overview', label: 'Overview' },
              { key: 'capabilities', label: 'Key Capabilities' },
              { key: 'impact', label: 'Productivity Impact' }
            ].map((tab) => (
              <button
                key={tab.key}
                className={`
                  px-6 py-3 mx-2 rounded-lg transition-all duration-300
                  ${activeSection === tab.key 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white' 
                    : 'bg-gray-800 text-purple-200 hover:bg-gray-700'}
                `}
                onClick={() => setActiveSection(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeSection === 'overview' && (
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-lg rounded-2xl p-8 border border-purple-500 border-opacity-30">
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent">
                Boosting Developer Productivity
              </h2>
              <p className="text-purple-200 mb-4">
                Code Cheetah within BlueGecko is designed to supercharge developer productivity by automating code generation and intelligently streamlining task allocation.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <Cpu className="text-purple-400 mr-3" size={24} />
                  <span className="text-purple-200">
                    Transform how your team manages data management and analytics workflows
                  </span>
                </div>
                <div className="flex items-center">
                  <Settings className="text-blue-400 mr-3" size={24} />
                  <span className="text-purple-200">
                    Enable developers to focus on innovation and complex problem-solving
                  </span>
                </div>
                <div className="flex items-center">
                  <Cast className="text-green-400 mr-3" size={24} />
                  <span className="text-purple-200">
                    Seamless integration with existing development processes
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Capabilities */}
          {activeSection === 'capabilities' && (
            <div className="grid md:grid-cols-3 gap-6">
              {keyCapabilities.map((capability) => (
                <button
                  key={capability.id}
                  className={`
                    p-6 rounded-2xl transition-all duration-300 text-left
                    ${activeCapability === capability.id 
                      ? 'bg-gray-700 border-2 border-purple-500 shadow-2xl' 
                      : 'bg-gray-800 bg-opacity-50 border border-purple-500 border-opacity-30 hover:bg-opacity-70'}
                  `}
                  onMouseEnter={() => setActiveCapability(capability.id)}
                  onMouseLeave={() => setActiveCapability(null)}
                  onClick={() => setActiveCapability(capability.id === activeCapability ? null : capability.id)}
                  type="button"
                >
                  <div className="flex items-center mb-4">
                    {capability.icon}
                    <h3 className="ml-4 text-xl font-semibold text-white">
                      {capability.title}
                    </h3>
                  </div>
                  <p className="text-purple-200">
                    {capability.description}
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Impact */}
          {activeSection === 'impact' && (
            <div className="space-y-10">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent text-center">
                3X Developer Productivity
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {impactItems.map((impact) => (
                  <button
                    key={impact.id}
                    className={`
                      p-6 rounded-2xl transition-all duration-300 text-left
                      ${activeImpact === impact.id 
                        ? 'bg-gray-700 border-2 border-purple-500 shadow-2xl' 
                        : 'bg-gray-800 bg-opacity-50 border border-purple-500 border-opacity-30 hover:bg-opacity-70'}
                    `}
                    onMouseEnter={() => setActiveImpact(impact.id)}
                    onMouseLeave={() => setActiveImpact(null)}
                    onClick={() => setActiveImpact(activeImpact === impact.id ? null : impact.id)}
                    type="button"
                  >
                    <div className="flex items-center mb-4">
                      {impact.icon}
                      <h3 className="ml-4 text-xl font-semibold text-white">
                        {impact.title}
                      </h3>
                    </div>
                    <p className="text-purple-200">
                      {impact.description}
                    </p>
                  </button>
                ))}
              </div>

              {/* Benefits */}
              <div className="mt-10">
                <h4 className="text-2xl font-bold mb-6 bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent text-center">
                  Key Benefits
                </h4>
                <div className="grid md:grid-cols-3 gap-6">
                  {benefitItems.map((benefit) => (
                    <button
                      key={benefit.id}
                      className={`
                        p-6 rounded-2xl transition-all duration-300 text-left
                        ${activeBenefit === benefit.id 
                          ? 'bg-gray-700 border-2 border-purple-500 shadow-2xl' 
                          : 'bg-gray-800 bg-opacity-50 border border-purple-500 border-opacity-30 hover:bg-opacity-70'}
                      `}
                      onMouseEnter={() => setActiveBenefit(benefit.id)}
                      onMouseLeave={() => setActiveBenefit(null)}
                      onClick={() => setActiveBenefit(activeBenefit === benefit.id ? null : benefit.id)}
                      type="button"
                    >
                      <div className="flex items-center mb-4">
                        {benefit.icon}
                        <h5 className="ml-4 text-xl font-semibold text-white">
                          {benefit.title}
                        </h5>
                      </div>
                      <p className="text-purple-200">
                        {benefit.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Call to Action */}
          <div className="mt-16 text-center">
            <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-purple-300 to-blue-300 bg-clip-text text-transparent">
              Transform Your Development Workflow
            </h3>
            <p className="text-purple-200 max-w-2xl mx-auto mb-8">
              Experience the power of AI-driven task management and code generation with Code Cheetah. 
              Revolutionize how your team works and innovates.
            </p>
            <button className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-semibold py-4 px-10 rounded-lg transition-all duration-300 flex items-center justify-center mx-auto group">
              Request a Demo
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
